import Service from '@ember/service';
import { service } from '@ember/service';
import { query } from '@warp-drive/utilities/json-api';
import ENV from 'tio-employee/config/environment';
import { buildTioAuthHeaders } from 'tio-data/utils/auth-headers';
import type { Store } from '@warp-drive/core';
import type PartnerService from './partner';
import type { TasProgramTemplateExtension as TasProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';
import type { CompanySettingQuicksight } from 'tio-data/data/resources/company-setting.type';
import type FeaturesService from 'tio-common/services/features';

type Dashboard = CompanySettingQuicksight['dashboards'][0];

function intersection<T>(arr1: T[], arr2: T[]) {
  const set2 = new Set(arr2);
  return arr1.filter((item) => set2.has(item));
}

/**
 * Provides data relating to the current partner's available QuickSight dasbhoards (aka reports).
 * Reports are viewable as part of the partner admin experience.
 *
 * @memberof services
 */
class PartnerDashboardsService extends Service {
  @service declare partner: PartnerService;
  @service declare store: Store;
  @service declare features: FeaturesService;

  /**
   * Returns an array of unfiltered QuickSight dashboards for the current company.
   * These are not to be displayed directly. See `reports` for that.
   */
  get dashboards() {
    return this.partner.companySettings?.quickSight?.dashboards || [];
  }

  filterDashboards = (dashboards: Dashboard[] = [], dashboardTypesToShow: string[] = []) => {
    return dashboards.filter((dashboard) => {
      return (
        dashboard.active &&
        dashboard.reportType?.at(0) !== 'AGENT' &&
        (dashboard.custom ||
          intersection(dashboardTypesToShow, dashboard.reportType).length !== 0 ||
          !dashboard.reportType)
      );
    });
  };

  fetchPartnerDashboardById = async (id: string) => {
    // WarpDrive migration (Phase 2.4/rule 4): replace adapter.headers + adapter.host
    // with buildTioAuthHeaders (no x-json-api-key-format — employee app is headerless).
    const headers = buildTioAuthHeaders({
      apiKey: ENV.apiKey,
      contentType: 'application/json',
    });

    const response = await fetch(
      `${ENV.apiHost}/companies/${this.partner.company?.id}/quicksight-dashboard/${id}`,
      { headers }
    );

    if (response.ok) {
      const dashboard = await response.json();
      return dashboard;
    } else {
      return response;
    }
  };

  loadAndFilterReportsForPartner = async () => {
    // TODO: why do we need to load plans here? Are they not loaded elsewhere?
    const plans = await this.partner.loadPlansForCurrentCompany();
    const hasContributions = this.partner.doPlansHaveContributions(plans);
    const hasWellness = this.partner.doPlansHaveWellness(plans);
    const hasSecure = this.partner.isSyfEnabled;
    const hasPslf = this.partner.isPslfEnabled;

    // WarpDrive builder read (Phase 2.4) — replaces store.query;
    // nested filter object flattened to bracket keys as required by QueryParamsSource.
    const companyId = this.partner.company?.id;
    const programTemplates = companyId
      ? await (async () => {
          const { content } = (await this.store.request(
            query<TasProgramTemplateModel>('tas-program-template', {
              'filter[state]': 'TAS.ProgramTemplateState.LAUNCHED',
              'filter[company]': companyId,
            })
          )) as { content: { data: TasProgramTemplateModel[] } };
          return content.data;
        })()
      : [];

    const hasTA = !!programTemplates.length;

    const dashboardTypesToShow = [
      ...(hasContributions ? ['CONTRIBUTION'] : []),
      ...(hasTA ? ['TUITION_ASSISTANCE'] : []),
      ...(hasWellness && this.features.isEnabled('wellnessInsights') ? ['WELLNESS'] : []),
      ...(hasSecure ? ['SECURE'] : []),
      ...(hasPslf ? ['PSLF'] : []),
    ];
    return this.filterDashboards(this.dashboards, dashboardTypesToShow);
  };
}

export default PartnerDashboardsService;
